/**
 * Equine - the richest first-class kind, and the template for how deep a kind
 * can go. Horses, ponies, mules, donkeys and hinnies are all this ONE kind
 * (ADR-66); the specific animal is `attributes.equineType` (added in the
 * equineType cut), never a separate lot kind. A pony is a height band, not a
 * species. Zebras are biologically equine too; their "exotic/regulated" status
 * is a policy dimension, not the lot kind.
 */

import { z } from 'zod';
import type { LotKindDefinition } from '../registry.js';
import { equineTypeSchema } from '../equine-types.js';

const registration = z
  .object({
    registry: z.string().min(1),
    registryConceptId: z.string().regex(/^registry:[a-z0-9]+(?:-[a-z0-9]+)*$/).optional(),
    number: z.string().min(1),
    registeredName: z.string().optional(),
  })
  .strict();

const attributes = z
  .object({
    // The equine SUBTYPE (ADR-66 PR 2). Absent = horse. This is the catalog
    // facet; Annie's identity fact `equine.type` maps here at materialization.
    // Zebra/zebra_hybrid are reserved (not enabled for any sale yet - their
    // "exotic/regulated" handling is a separate policy dimension, not the kind).
    equineType: equineTypeSchema.optional(),
    sex: z.enum(['stallion', 'mare', 'gelding', 'colt', 'filly']).optional(),
    // Customer-facing sex term (Molly mule, John mule, Jack, Jennet, ...), kept
    // SEPARATE from the machine `sex` above. A John mule is NOT auto-gelded.
    sexTerm: z.string().optional(),
    color: z.string().optional(),
    colorConceptId: z.string().regex(/^color:[a-z0-9]+(?:-[a-z0-9]+)*$/).optional(),
    foalYear: z.number().int().min(1980).max(2100).optional(),
    /** Height in hands (base-4: the fraction is inches, 0–3; e.g. 15.2). A hand
     *  is 4 inches, so 15.4 is not a height - it is 16.0. 5.0–21.0 spans minis
     *  to the tallest drafts. `heightInches` carries the exact integer inches.
     *  (@hipnumber/equine-knowledge is the authority for parsing/validation;
     *  the base-4 rule is inlined here to keep lot-kinds dependency-free.) */
    heightHands: z
      .number()
      .min(5)
      .max(21)
      .refine((h) => Math.round((h - Math.floor(h)) * 10) <= 3, {
        message: 'height is in hands; the fraction is inches (0–3), so 15.4 is not valid - use 15.3 or 16',
      })
      .optional(),
    heightInches: z.number().int().min(20).max(84).optional(),
    breed: z.string().optional(),
    breedConceptId: z.string().regex(/^breed:[a-z0-9]+(?:-[a-z0-9]+)*$/).optional(),
    /** Explicit cross structure when seller/auction supplied cross notation. */
    breedCrossParentConceptIds: z.array(z.string().regex(/^breed:[a-z0-9]+(?:-[a-z0-9]+)*$/)).min(2).max(4).optional(),
    registration: registration.optional(),
    /** Seller/auction registration-paper status; distinct from an actual registry record. */
    registrationStatus: z.string().optional(),
    registrationStatusConceptId: z.string().regex(/^auction_term:[a-z0-9]+(?:-[a-z0-9]+)*$/).optional(),
    sire: z.string().optional(),
    dam: z.string().optional(),
    damsire: z.string().optional(),
    disciplines: z.array(z.string()).optional(),
    disciplineConceptIds: z.array(z.string().regex(/^discipline:[a-z0-9]+(?:-[a-z0-9]+)*$/)).optional(),
    /** Raw seller/auction gait representation; never inferred from breed. */
    gaitText: z.string().optional(),
    gaitConceptIds: z.array(z.string().regex(/^gait:[a-z0-9]+(?:-[a-z0-9]+)*$/)).optional(),
    underSaddle: z.boolean().optional(),
    kidSafe: z.boolean().optional(),
    inFoal: z.object({ bredTo: z.string().optional(), dueDate: z.string().optional() }).strict().optional(),
    broodmare: z.boolean().optional(),
  })
  .strict();

export const equine: LotKindDefinition = {
  kind: 'equine',
  label: 'Horse / Pony',
  attributes,
  releaseChecklist: [
    { docType: 'coggins', label: 'Negative Coggins test', required: true },
    { docType: 'health_cert', label: 'Health certificate (CVI)', required: true },
    { docType: 'registration_papers', label: 'Registration papers', required: false, requiredWhen: (a) => a['registration'] !== undefined },
    { docType: 'transfer_report', label: 'Signed transfer report', required: false, requiredWhen: (a) => a['registration'] !== undefined },
  ],
  facets: [
    { path: 'equineType', label: 'Type', type: 'enum' },
    { path: 'sex', label: 'Sex', type: 'enum' },
    { path: 'sexTerm', label: 'Sex term', type: 'string' },
    { path: 'breed', label: 'Breed', type: 'string' },
    { path: 'breedConceptId', label: 'Breed concept', type: 'string' },
    { path: 'breedCrossParentConceptIds', label: 'Breed cross parents', type: 'string' },
    { path: 'color', label: 'Color', type: 'string' },
    { path: 'colorConceptId', label: 'Color concept', type: 'string' },
    { path: 'heightHands', label: 'Height', type: 'number', unit: 'HH' },
    { path: 'foalYear', label: 'Foal year', type: 'number' },
    { path: 'disciplines', label: 'Discipline', type: 'string' },
    { path: 'disciplineConceptIds', label: 'Discipline concepts', type: 'string' },
    { path: 'gaitText', label: 'Seller-represented gait', type: 'string' },
    { path: 'gaitConceptIds', label: 'Gait concepts', type: 'string' },
    { path: 'underSaddle', label: 'Under saddle', type: 'boolean' },
    { path: 'inFoal', label: 'In foal', type: 'boolean' },
    { path: 'registration.registry', label: 'Registry', type: 'string' },
    { path: 'registration.registryConceptId', label: 'Registry concept', type: 'string' },
    { path: 'registrationStatus', label: 'Registration status', type: 'string' },
  ],
  // The lot page's rail in a horse buyer's order (ADR-69). The storefront's
  // hand-curated equine rail (web-sites lib/details.ts horseDetails) renders
  // these same paths in this same order with its own formatting (coat
  // swatch, age, registered name); a test there holds the two together. The
  // ontology ids (breedConceptId and friends) are deliberately absent: they
  // are facets for search, never something to sell a horse with.
  details: [
    { path: 'sex', label: 'Sex', type: 'enum' },
    { path: 'breed', label: 'Breed', type: 'string' },
    { path: 'color', label: 'Color', type: 'string' },
    { path: 'heightHands', label: 'Height', type: 'number', unit: 'HH' },
    { path: 'foalYear', label: 'Foaled', type: 'number' },
    { path: 'disciplines', label: 'Discipline', type: 'string' },
    { path: 'gaitText', label: 'Gait', type: 'string' },
    { path: 'underSaddle', label: 'Under saddle', type: 'boolean' },
    { path: 'inFoal', label: 'In foal', type: 'boolean' },
    { path: 'kidSafe', label: 'Kid safe', type: 'boolean' },
    { path: 'registration.registry', label: 'Registry', type: 'string' },
    { path: 'registrationStatus', label: 'Registration status', type: 'string' },
  ],
};
